import { useCallback, useEffect, useMemo, useState } from 'react'
import { saveBlobAsFile } from '../../../../api/apiUtils.js'
import {
  downloadVendorDetailsData,
  searchFilterVendor,
  deleteVendorMaster,
  updateVendorMaster
} from '../../../../api/vendorMasterApi.js'
import { VENDOR_DETAILS_COLUMNS, vendorSchema } from '../../../../constants/define.jsx'
import { getValidCriteria, DEFAULT_PAGE } from '../../../../constants/index.js'
import { useSnackbar } from '../../../../contexts/SnackbarProvider.jsx'
import { CommonTable } from '../../../common/index.js'

const VendorDetails = ({
  isEditMode,
  searchCOList,
  tanUuid,
  downloadExcel,
  searchAction,
  onActiveTabData,
  deleteData,
  isCloseEditMode,
  editingData,
  onFieldChange,
  visibleColumns,
  triggerSave,
  setTriggerSave,
  setEditingData,
  setIsEditMode,
  setDownloadExcel,
  setDisabled,
  setConfirmConfig,
  setConfirmOpen,
  pendingDelete,
  confirmOpen,
  setDeleteData
}) => {
  console.log(tanUuid, "tanUuid");

  const [tableData, setTableData] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)

  const filteredColumns = useMemo(
    () => VENDOR_DETAILS_COLUMNS.filter(col => visibleColumns?.includes(col.id)),
    [visibleColumns]
  )

  const { showSnackbar } = useSnackbar()

  const createPayload = (currentPage, pageSize, searchCOList) => ({
    pageFilter: {
      currentPage,
      pageSize,
      sortField: 'vendorName',
      sortOrder: 'ASC',
      includeCount: true,
    },
    userRoleType: 'ADMIN',
    searchCOList: getValidCriteria(searchCOList),
  })

  const stablePayload = useMemo(
    () => createPayload(currentPage, itemsPerPage, searchCOList),
    [currentPage, itemsPerPage, searchCOList]
  )

  const fetchVendorDetailsTableData = useCallback(async () => {
    if (!tanUuid) return
    setLoading(true)
    const result = await searchFilterVendor(stablePayload, tanUuid)
    if (result?.success) {
      setTableData(result.data.content)
      setTotalItems(result.data.totalElements)
    } else {
      setTableData([])
      if (result?.error) showSnackbar(result.error, 'error')
    }
    setLoading(false)
  }, [stablePayload, tanUuid])
  const downloadVendorDetailsExcel = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return;
    }
    const payload = {
      pageFilter: {
        currentPage,
        pageSize: DEFAULT_PAGE,
        sortField: 'vendorName',
        sortOrder: 'ASC',
        includeCount: true,
      },
      userRoleType: 'ADMIN',
      searchCOList: getValidCriteria(searchCOList),
    }
    const result = await downloadVendorDetailsData(payload, tanUuid)
    if (result?.success && result?.data) saveBlobAsFile(result.data, 'VendorDetails.xlsx')
    else if (result?.error) showSnackbar(result.error, 'error')
  }

  const deleteVendorMasterData = useCallback(async () => {
    if (selectedRows.length === 0) {
      showSnackbar('Please select at least one row', 'error')
      return
    }

    const payload = selectedRows.map(uuid => ({ uuid }))
    const result = await deleteVendorMaster(payload)
    if (result?.success) {
      showSnackbar('Vendor deleted successfully', 'success')
      setSelectedRows([])
      await fetchVendorDetailsTableData()
    } else if (result?.error) {
      showSnackbar(result.error, 'error')
    }
  }, [selectedRows, fetchVendorDetailsTableData, showSnackbar])

  const handleSelectRow = uuid => {
    setSelectedRows(prev =>
      prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
    )
    setDownloadExcel(0)
    setDeleteData(0)
  }

  const handleSelectAll = e => {
    setSelectedRows(e.target.checked ? tableData.map(r => r.uuid) : [])
    setDownloadExcel(0)
    setDeleteData(0)
  }

  const handlePageChange = page => {
    setCurrentPage(page)
    setDownloadExcel(0)
  }
  const handleItemsPerPageChange = pageSize => {
    setItemsPerPage(pageSize)
    setCurrentPage(1)
    setDownloadExcel(0)
  }

  useEffect(() => { fetchVendorDetailsTableData() }, [fetchVendorDetailsTableData])
  // useEffect(() => { onActiveTabData({ type: 'editEnabled', value: selectedRows.length > 0 }) }, [selectedRows, onActiveTabData])
  useEffect(() => { if (isCloseEditMode) setSelectedRows([]) }, [isCloseEditMode])
  useEffect(() => {
    if (downloadExcel) downloadVendorDetailsExcel()
  }, [downloadExcel, downloadVendorDetailsExcel])

  useEffect(() => {
    if (selectedRows.length == 0) {
      setDisabled(true)
      return
    } else {
      setDisabled(false)
    }
    if (deleteData) {
      deleteVendorMasterData()
    }
  }, [deleteData, deleteVendorMasterData])

  useEffect(() => {
    if (selectedRows.length === 0) {
      setDisabled(true)
      return
    } else {
      setDisabled(false)
    }

    if (confirmOpen) {

      setConfirmConfig({
        title: 'Are you sure you want to delete selected record?',
        message: 'On Deletion of vendor, all transactional data associated with that vendor will not be available. Do you want to continue?',
        confirmText: 'Proceed',
      })

    }
  }, [deleteData, tableData, selectedRows, confirmOpen])


  useEffect(() => { setCurrentPage(1) }, [searchAction])
  // Save edited rows
  useEffect(() => {
    if (!triggerSave) return

    const entries = Object.entries(editingData || {})
    if (!entries.length) {
      showSnackbar('No rows to save', 'error')
      setTriggerSave(0)
      return
    }

    const validRowsPayload = []    // payload items like { uuid, field1, field2 }
    const invalidMessages = []     // collect messages (one string per issue)


    entries.forEach(([uuid, changes], idx) => {
      const originalRow = tableData.find(r => r.uuid === uuid)

      if (!originalRow) {
        const msg = `Row for UUID ${uuid} not found on current page`
        invalidMessages.push(msg)
        return
      }

      const rowNumber =
        (tableData.findIndex(r => r.uuid === uuid) >= 0
          ? tableData.findIndex(r => r.uuid === uuid) + 1
          : idx + 1)

      // sanitize only edited fields
      const sanitizedChanges = { ...changes }
      if (sanitizedChanges.contactNumber !== undefined && sanitizedChanges.contactNumber !== null) {
        sanitizedChanges.contactNumber = String(sanitizedChanges.contactNumber).replace(/\D/g, "")
      }

      // Validate merged row (ensures schema rules that depend on other fields still pass)
      const mergedRow = { ...originalRow, ...sanitizedChanges }
      const result = vendorSchema.safeParse(mergedRow)

      if (result.success) {
        // prepare payload as uuid + only edited (sanitized) fields
        validRowsPayload.push({
          uuid,
          ...sanitizedChanges,
        })
      } else {
        const issues = result.error?.issues || []

        if (issues.length === 0) {
          invalidMessages.push(`Row ${rowNumber}: Validation failed`)
        } else {
          issues.forEach(issue => {
            const field = issue.path?.[0] ?? 'unknown'
            const msg = `Row ${rowNumber} [${field}]: ${issue.message}`
            invalidMessages.push(msg)
          })
        }
      }
    })

    if (invalidMessages.length > 0) {
      const delay = 700 // ms between snackbars (tweak as needed)
      invalidMessages.forEach((m, i) => {
        setTimeout(() => showSnackbar(m, 'error'), i * delay)
      })

      setTriggerSave(0)
      return
    }

    if (validRowsPayload.length === 0) {
      showSnackbar('No valid changes to update', 'error')
      setTriggerSave(0)
      return
    }

    const saveAll = async () => {
      try {
        const result = await updateVendorMaster(validRowsPayload)

        if (result?.data?.success) {
          showSnackbar('Vendors updated successfully', 'success')
          // remove only saved rows from editingData
          const updatedEditingData = { ...editingData }
          validRowsPayload.forEach(r => delete updatedEditingData[r.uuid])
          setEditingData(updatedEditingData)

          setSelectedRows([])
          setIsEditMode && setIsEditMode(false) // if available in scope
          await fetchVendorDetailsTableData()
        } else if (result?.error) {
          showSnackbar(result.error, 'error')
        } else {
          showSnackbar(result.data.data, 'error')
        }
      } catch (err) {
        showSnackbar('Error updating vendors', 'error')
      } finally {
        setTriggerSave(0)
      }
    }

    saveAll()
  }, [
    triggerSave,
    tableData,
    fetchVendorDetailsTableData,
    setEditingData,
    setTriggerSave,
    setSelectedRows,
    setIsEditMode,
  ])


  return (
    <CommonTable
      columns={filteredColumns}
      data={tableData}
      editMode={isEditMode}
      loading={loading}
      itemsPerPage={itemsPerPage}
      totalItems={totalItems}
      currentPage={currentPage}
      showActions={false}
      showCheckbox={true}
      showFooter={true}
      showItemsPerPage={true}
      showTotalItems={true}
      showPageNumbers={true}
      selectedItems={selectedRows}
      emptyMessage="No data found"
      onSelectItem={handleSelectRow}
      onSelectAll={handleSelectAll}
      onPageChange={handlePageChange}
      onItemsPerPageChange={handleItemsPerPageChange}
      editingData={editingData}
      onFieldChange={onFieldChange}
    />
  )
}

export default VendorDetails
